import { observer } from "mobx-react-lite";
import { DeleteCollectionsModalStore } from "@/components/modal/delete-collections/delete-collections-modal-store";
import { pluralize } from "@/modules/pluralize";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSModalDialog,
  MDSDialogSlotName,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSDialogTextContent,
  MDSCollectionIcon,
} from "@/mds";
import styled from "@emotion/styled";

interface Props {
  state: DeleteCollectionsModalStore;
}

export const DeleteCollectionsModal = observer<Props>(({ state }) => {
  if (!state.collections.length) return null;

  const primaryTitle = state.collections[0].label;
  const secondaryTitle = getSecondaryTitle(state.collections);
  const sharedCollections = state.collections.filter(collection => collection.isShared);
  const isAllShared = sharedCollections.length === state.collections.length;
  const isAnyShared = sharedCollections.length > 0;
  const prefix = getTitlePrefix({
    isAnyShared,
    isAllShared,
    collectionCount: state.collections.length,
  });

  return (
    <MDSModalDialog isOpen={state.isOpen} handleClose={() => state.close()}>
      <Title slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
        <TitlePrefix>{prefix}</TitlePrefix>{" "}
        <TitleCollection data-delete-collection-title="collection">
          <TitleCollectionIcon>
            <MDSCollectionIcon size={16} boxSize={16} collectionId={state.collections[0].id} />
          </TitleCollectionIcon>
          <TitleCollectionName>{primaryTitle}</TitleCollectionName>
        </TitleCollection>
        {secondaryTitle ? (
          <>
            {" "}
            <span>{secondaryTitle}</span>
            {"?"}
          </>
        ) : (
          "?"
        )}
      </Title>

      <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
        <MDSText variant={MDSTextVariant.ParagraphS}>
          {isAnyShared ? (
            <>
              {!isAllShared && <span>Some collections you selected were shared with others. </span>}
              If you delete{" "}
              {pluralize(state.collections.length, "this collection", "these collections")},
              collaborators will immediately lose access to{" "}
              {pluralize(state.collections.length, "it", "them")}.{" "}
            </>
          ) : (
            <>
              Make sure you want to permanently delete{" "}
              {pluralize(state.collections.length, "this collection", "these collections")}.{" "}
            </>
          )}
        </MDSText>
        <MDSText variant={MDSTextVariant.ParagraphS} weight={MDSTextWeight.SemiBold}>
          You cannot undo this action.
        </MDSText>
      </MDSDialogTextContent>

      <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
        <MDSButton variant={MDSButtonVariant.Default} onClick={() => state.close()}>
          Cancel
        </MDSButton>
        <MDSButton
          variant={MDSButtonVariant.DangerFilled}
          onClick={async () => await state.handleSubmit()}
        >
          Delete {pluralize(state.collections.length, "collection", "collections")}
        </MDSButton>
      </MDSButtonGroup>
    </MDSModalDialog>
  );
});

const getSecondaryTitle = (collections: CollectionObservable[]) => {
  if (collections.length === 1) return "";

  return `and ${collections.length - 1} other ${pluralize(collections.length - 1, "collection", "collections")}`;
};

const getTitlePrefix = ({
  isAnyShared,
  isAllShared,
  collectionCount,
}: {
  isAnyShared: boolean;
  isAllShared: boolean;
  collectionCount: number;
}) => {
  if (isAnyShared && collectionCount === 1) {
    return "Delete shared collection";
  }

  if (isAnyShared && isAllShared) {
    return "Delete shared";
  }

  return "Delete";
};

const Title = styled(MDSText)`
  /* Shared dialog header padding clips this line-clamped title. */
  &&& {
    padding-block: 0;
  }

  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  max-height: 2lh;
`;

const TitlePrefix = styled.span`
  white-space: nowrap;
`;

const TitleCollection = styled.span`
  box-sizing: border-box;
  display: inline-block;
  max-width: 100%;
  min-width: 0;
  padding-left: calc(16px + ${({ theme }) => theme.spacing["1"]});
  position: relative;
`;

const TitleCollectionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  height: 1lh;
  left: 0;
  position: absolute;
  top: 0;
`;

const TitleCollectionName = styled.span`
  overflow-wrap: anywhere;
`;
